import { Injectable } from '@angular/core';

const DRAG_THRESHOLD = 5;
const INIT_FLAG = 'chatDragInit';
const TAB_BAR_CLEARANCE = 12;

@Injectable({ providedIn: 'root' })
export class ChatBubbleDragService {
  private hidden = false;

  // The button lives inside the shadow DOM of <chat-widget>.
  // getElementById / contains() do not cross shadow boundaries —
  // we must use shadowRoot.querySelector and composedPath().
  private getBtn(): HTMLElement | null {
    const host = document.querySelector('chat-widget');

    return (
      (host?.shadowRoot?.querySelector(
        '#chatbot-widget-window-button',
      ) as HTMLElement | null) ?? null
    );
  }

  setVisibility(visible: boolean): void {
    this.hidden = !visible;

    const host = document.querySelector('chat-widget') as HTMLElement | null;

    if (!host) {
      return;
    }

    host.style.display = visible ? '' : 'none';

    if (visible) {
      // Reset so applyToBtn recalculates position with correct dimensions
      // after the widget was hidden (offsetWidth/offsetHeight are 0 when hidden).
      const btn = this.getBtn();

      if (btn) {
        delete btn.dataset['draggable'];
      }

      this.init();
    }
  }

  init(): void {
    this.applyToBtn();

    if (document.body.dataset[INIT_FLAG] === 'true') {
      return;
    }

    const btn = this.getBtn();

    if (!btn) {
      return;
    }

    document.body.dataset[INIT_FLAG] = 'true';
    this.registerListeners();
  }

  private applyToBtn(): void {
    const host = document.querySelector('chat-widget') as HTMLElement | null;

    if (this.hidden) {
      if (host) {
        host.style.display = 'none';
      }

      return;
    }

    const btn = this.getBtn();

    if (!btn) {
      return;
    }

    // Re-apply protective styles on every call — the widget may re-render
    // and clear them, which would let the browser take over the gesture.
    btn.style.cursor = 'grab';
    btn.style.touchAction = 'none';

    (
      btn.style as CSSStyleDeclaration & { webkitTouchCallout: string }
    ).webkitTouchCallout = 'none';

    btn.draggable = false;

    btn.querySelectorAll<HTMLElement>('*').forEach((el) => {
      el.draggable = false;
      el.style.touchAction = 'none';
    });

    // Default position is set only once per button instance.
    if (btn.dataset['draggable'] === 'true') {
      return;
    }

    btn.dataset['draggable'] = 'true';

    const hasMobileTabBar = window.innerWidth < 768;
    const horizontalOffset = hasMobileTabBar ? 22 : 30;
    const verticalOffset = this.getVerticalOffset(hasMobileTabBar ? 85 : 15);

    btn.style.left = `${window.innerWidth - btn.offsetWidth - horizontalOffset}px`;
    btn.style.right = 'auto';
    btn.style.top = `${window.innerHeight - btn.offsetHeight - verticalOffset}px`;
    btn.style.bottom = 'auto';
  }

  // The tab bar floats above the viewport bottom edge (incl. safe-area inset),
  // so the bubble's default position must clear the bar's measured top edge.
  private getVerticalOffset(fallback: number): number {
    const tabBar = document.querySelector('app-tab-bar ion-tab-bar');

    if (!tabBar) {
      return fallback;
    }

    const rect = tabBar.getBoundingClientRect();

    if (rect.height === 0) {
      return fallback;
    }

    return Math.max(fallback, window.innerHeight - rect.top + TAB_BAR_CLEARANCE);
  }

  private registerListeners(): void {
    let startX = 0,
      startY = 0,
      startLeft = 0,
      startTop = 0;

    let dragging = false;
    let moved = false;

    document.addEventListener(
      'dragstart',
      (e) => {
        const b = this.getBtn();

        if (b && e.composedPath().includes(b)) {
          e.preventDefault();
        }
      },
      true,
    );

    document.addEventListener(
      'pointerdown',
      (e) => {
        const b = this.getBtn();

        if (!b || !e.composedPath().includes(b)) {
          return;
        }

        const rect = b.getBoundingClientRect();
        startX = e.clientX;
        startY = e.clientY;
        startLeft = rect.left;
        startTop = rect.top;
        dragging = true;
        moved = false;
        b.style.cursor = 'grabbing';
        e.preventDefault();
      },
      true,
    );

    document.addEventListener(
      'pointermove',
      (e) => {
        if (!dragging) {
          return;
        }

        const b = this.getBtn();

        if (!b) {
          dragging = false;

          return;
        }

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        if (
          !moved &&
          Math.abs(dx) < DRAG_THRESHOLD &&
          Math.abs(dy) < DRAG_THRESHOLD
        ) {
          return;
        }

        moved = true;
        b.style.left = `${Math.max(0, Math.min(window.innerWidth - b.offsetWidth, startLeft + dx))}px`;
        b.style.right = 'auto';
        b.style.top = `${Math.max(0, Math.min(window.innerHeight - b.offsetHeight, startTop + dy))}px`;
        b.style.bottom = 'auto';
        e.preventDefault();
      },
      { capture: true, passive: false },
    );

    const endDrag = () => {
      if (!dragging) {
        return;
      }

      dragging = false;
      const b = this.getBtn();

      if (!b) {
        return;
      }

      b.style.cursor = 'grab';
    };

    document.addEventListener('pointerup', endDrag, true);
    // pointercancel fires on mobile when the browser takes over the gesture
    // (e.g. scroll, long-press menu). Treat it the same as pointerup so the
    // drag state is cleaned up.
    document.addEventListener('pointercancel', endDrag, true);

    let resizeTimer: ReturnType<typeof setTimeout> | null = null;

    window.addEventListener('resize', () => {
      if (resizeTimer !== null) {
        clearTimeout(resizeTimer);
      }

      resizeTimer = setTimeout(() => {
        const btn = this.getBtn();

        if (btn) {
          delete btn.dataset['draggable'];
        }

        this.applyToBtn();
      }, 150);
    });

    document.addEventListener(
      'click',
      (e) => {
        const b = this.getBtn();

        if (moved && b && e.composedPath().includes(b)) {
          e.stopPropagation();
          e.preventDefault();
          moved = false;
        }
      },
      true,
    );
  }
}
